import { combineReducers } from 'redux';
import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { createWrapper } from 'next-redux-wrapper';
import { Persistor, persistReducer, persistStore } from 'redux-persist';
import { baseApi } from '../services/base-api';
import { participantBaseApi } from '../services/public-participants';
import ventureReducer from '../slices/venture';
import solutionReducer from '../slices/solution';
import ideSettingsReducer from '../slices/ide-settings';
import { PERSIST_CONFIG } from './persistence';

type WithPersistor<T> = T & { persistor?: Persistor };

const rootReducer = combineReducers({
  [baseApi.reducerPath]: baseApi.reducer,
  [participantBaseApi.reducerPath]: participantBaseApi.reducer,
  venture: ventureReducer,
  solution: solutionReducer,
  ideSettings: ideSettingsReducer,
});

export function makeServersideStore() {
  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware()
        .concat(baseApi.middleware)
        .concat(participantBaseApi.middleware),
  });

  setupListeners(store.dispatch);

  return store;
}

function makeClientsideStore() {
  const persistedReducer = persistReducer(PERSIST_CONFIG, rootReducer);
  const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: { ignoredActions: ['persist/PERSIST'] },
      })
        .concat(baseApi.middleware)
        .concat(participantBaseApi.middleware),
  });

  setupListeners(store.dispatch);

  (store as WithPersistor<typeof store>).persistor = persistStore(store);

  return store;
}

function makeStore() {
  return typeof window === 'undefined'
    ? makeServersideStore()
    : makeClientsideStore();
}

export type AppStore = WithPersistor<ReturnType<typeof makeStore>>;
export type RootState = ReturnType<AppStore['getState']>;
export type AppDispatch = AppStore['dispatch'];

export const wrapper = createWrapper<AppStore>(makeStore, {
  debug: process.env.NODE_ENV !== 'production',
  // Issue: https://github.com/vercel/next.js/discussions/11209
  // To avoid app crash when having `undefined` in redux state, we need to
  // provide our own way to serialize state that can handle such cases
  serializeState: (state) => JSON.stringify(state),
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument,@typescript-eslint/no-unsafe-return
  deserializeState: (state) => JSON.parse(state),
});
